import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Church, Plus, Trash2, Save, MapPin, Mail, Phone, Globe, Clock, Cross, Calendar, Link2, Copy, RefreshCw, Check, Sun, Pencil, ChevronUp, ChevronDown, BookOpen, Building2, LocateFixed } from "lucide-react";
import { getParish, updateParish, generateInviteLink, getParishInvite, revokeInviteLink, getDioceses, geocodeParish } from "@/lib/api";
import { toast } from "sonner";
import { MassScheduleDialog, ReconciliationScheduleDialog, AdorationScheduleDialog, RITES } from "@/components/parish";
import DioceseSearchSelect from "@/components/parish/DioceseSearchSelect";
import RichTextEditor from "@/components/RichTextEditor";
import ParishStaffSection from "@/components/parish/ParishStaffSection";

export default function ParishSettingsTab({ user }) {
  const [parish, setParish] = useState(null);
  const [geocoding, setGeocoding] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [invite, setInvite] = useState(null);
  const [generatingInvite, setGeneratingInvite] = useState(false);
  const [copied, setCopied] = useState(false);
  const [diocesesData, setDiocesesData] = useState({ countries: [], dioceses_by_country: {} });
  const [selectedCountry, setSelectedCountry] = useState("");
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    address: "",
    office_email: "",
    office_phone: "",
    website: "",
    rite: "",
    diocese: "",
    diocese_country: "",
    mass_schedules: [],
    reconciliation_schedules: [],
    adoration_schedules: [],
    staff: []
  });

  useEffect(() => {
    loadParish();
    loadInvite();
    loadDioceses();
  }, [user.parish_id]);

  const loadDioceses = async () => {
    try {
      const res = await getDioceses();
      setDiocesesData(res.data);
    } catch (err) {
      console.error("Failed to load dioceses:", err);
    }
  };

  const loadParish = async () => {
    if (!user.parish_id) return;
    setLoading(true);
    try {
      const res = await getParish(user.parish_id);
      setParish(res.data);
      setFormData({
        name: res.data.name || "",
        description: res.data.description || "",
        address: res.data.address || res.data.location || "",
        office_email: res.data.office_email || "",
        office_phone: res.data.office_phone || "",
        website: res.data.website || "",
        rite: res.data.rite || "",
        diocese: res.data.diocese || "",
        diocese_country: res.data.diocese_country || "",
        mass_schedules: res.data.mass_schedules || [],
        reconciliation_schedules: res.data.reconciliation_schedules || [],
        adoration_schedules: res.data.adoration_schedules || [],
        staff: res.data.staff || []
      });
      if (res.data.diocese_country) {
        setSelectedCountry(res.data.diocese_country);
      }
    } catch (err) {
      toast.error("Failed to load parish data");
    }
    setLoading(false);
  };

  const loadInvite = async () => {
    if (!user.parish_id) return;
    try {
      const res = await getParishInvite(user.parish_id);
      setInvite(res.data.invite);
    } catch (err) {
      // No existing invite
    }
  };

  const handleGenerateInvite = async () => {
    setGeneratingInvite(true);
    try {
      const res = await generateInviteLink(user.parish_id);
      setInvite({
        invite_code: res.data.invite_code,
        expires_at: res.data.expires_at,
        uses: 0
      });
      toast.success("Invite link generated!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to generate invite link");
    }
    setGeneratingInvite(false);
  };

  const handleRevokeInvite = async () => {
    if (!invite) return;
    try {
      await revokeInviteLink(user.parish_id, invite.invite_code);
      setInvite(null);
      toast.success("Invite link revoked");
    } catch (err) {
      toast.error("Failed to revoke invite link");
    }
  };

  const copyInviteLink = () => {
    if (!invite) return;
    const link = `${window.location.origin}/join/${invite.invite_code}`;
    navigator.clipboard.writeText(link);
    setCopied(true);
    toast.success("Invite link copied to clipboard!");
    setTimeout(() => setCopied(false), 2000);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateParish(user.parish_id, formData);
      toast.success("Parish updated successfully!");
      // Reload parish to get updated data including any auto-geocoded coordinates
      await loadParish();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update parish");
    }
    setSaving(false);
  };

  const handleGeocode = async () => {
    if (!formData.address || !formData.address.trim()) {
      toast.error("Please enter an address first");
      return;
    }
    
    setGeocoding(true);
    try {
      const res = await geocodeParish(user.parish_id, formData.address.trim());
      toast.success(`Address geocoded! Coordinates: ${res.data.coordinates.lat.toFixed(4)}, ${res.data.coordinates.lng.toFixed(4)}`);
      // Reload parish to show coordinates
      await loadParish();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to geocode address. Please check the address is complete and valid.");
    }
    setGeocoding(false);
  };

  // Schedule helper functions
  const addSchedule = (type, schedule) => {
    setFormData(prev => ({
      ...prev,
      [type]: [...prev[type], schedule]
    }));
  };

  const editSchedule = (type, idx, schedule) => {
    setFormData(prev => {
      const updated = [...prev[type]];
      updated[idx] = schedule;
      return { ...prev, [type]: updated };
    });
  };

  const removeSchedule = (type, idx) => {
    setFormData(prev => ({
      ...prev,
      [type]: prev[type].filter((_, i) => i !== idx)
    }));
  };

  const duplicateSchedule = (type, idx) => {
    setFormData(prev => {
      const original = prev[type][idx];
      const duplicate = { ...original }; // Create a copy
      const newArr = [...prev[type]];
      newArr.splice(idx + 1, 0, duplicate); // Insert after the original
      return { ...prev, [type]: newArr };
    });
    toast.success("Schedule duplicated");
  };

  const moveSchedule = (type, idx, direction) => {
    setFormData(prev => {
      const arr = [...prev[type]];
      const newIdx = direction === 'up' ? idx - 1 : idx + 1;
      if (newIdx < 0 || newIdx >= arr.length) return prev;
      [arr[idx], arr[newIdx]] = [arr[newIdx], arr[idx]];
      return { ...prev, [type]: arr };
    });
  };

  if (loading) {
    return (
      <div className="text-center py-8 text-[#6B665E] font-['Nunito']">Loading parish settings...</div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Invite Link Card */}
      <Card className="bg-white border-[#E5E0D5] shadow-sm border-l-4 border-l-[#D4AF37]">
        <CardHeader className="pb-3">
          <CardTitle className="font-['Crimson_Text'] text-xl text-[#2D2A26] flex items-center gap-2">
            <Link2 className="w-5 h-5 text-[var(--liturgical-secondary)]" />
            Invite Link
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-[#6B665E] font-['Nunito'] mb-4">
            Share this link to invite people to join your parish directly, without needing approval.
          </p>
          
          {invite ? (
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <Input 
                  readOnly
                  value={`${window.location.origin}/join/${invite.invite_code}`}
                  className="border-[#E5E0D5] font-['Nunito'] text-sm bg-[#F9F7F2]"
                />
                <Button 
                  onClick={copyInviteLink}
                  variant="outline"
                  size="sm"
                  className="border-[var(--liturgical-primary)] text-[var(--liturgical-primary)] hover:bg-[var(--liturgical-primary)]/10 font-['Nunito'] px-3"
                  data-testid="copy-invite-btn"
                >
                  {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                </Button>
              </div>
              <div className="flex items-center justify-between text-xs text-[#6B665E] font-['Nunito']">
                <span>Used {invite.uses || 0} times</span>
                <span>Expires: {new Date(invite.expires_at).toLocaleDateString()}</span>
              </div>
              <div className="flex gap-2">
                <Button 
                  onClick={handleGenerateInvite}
                  variant="outline"
                  size="sm"
                  disabled={generatingInvite}
                  className="border-[#6B665E] text-[#6B665E] hover:bg-[#F0EBE0] font-['Nunito'] text-xs"
                >
                  <RefreshCw className={`w-3 h-3 mr-1 ${generatingInvite ? 'animate-spin' : ''}`} />
                  Generate New
                </Button>
                <Button 
                  onClick={handleRevokeInvite}
                  variant="ghost"
                  size="sm"
                  className="text-red-600 hover:bg-red-50 font-['Nunito'] text-xs"
                >
                  <Trash2 className="w-3 h-3 mr-1" />
                  Revoke
                </Button>
              </div>
            </div>
          ) : (
            <Button 
              onClick={handleGenerateInvite}
              disabled={generatingInvite}
              className="bg-[var(--liturgical-secondary)] hover:brightness-90 text-white font-['Nunito']"
              data-testid="generate-invite-btn"
            >
              {generatingInvite ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Generating...
                </>
              ) : (
                <>
                  <Link2 className="w-4 h-4 mr-2" />
                  Generate Invite Link
                </>
              )}
            </Button>
          )}
        </CardContent>
      </Card>

      {/* Contact Information */}
      <Card className="bg-white border-[#E5E0D5] shadow-sm">
        <CardHeader className="pb-3">
          <CardTitle className="font-['Crimson_Text'] text-xl text-[#2D2A26] flex items-center gap-2">
            <MapPin className="w-5 h-5 text-[var(--liturgical-primary)]" />
            Parish Information
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label className="text-xs font-['Nunito'] text-[#6B665E]">Parish Name</Label>
              <Input 
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                className="border-[#E5E0D5] font-['Nunito'] text-sm"
                data-testid="parish-name-input"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-['Nunito'] text-[#6B665E]">Address</Label>
              <div className="flex gap-2">
                <Input 
                  value={formData.address}
                  onChange={(e) => setFormData({...formData, address: e.target.value})}
                  placeholder="123 Church St, City, State 12345"
                  className="border-[#E5E0D5] font-['Nunito'] text-sm flex-1"
                  data-testid="parish-address-input"
                />
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={handleGeocode}
                  disabled={geocoding || !formData.address}
                  className="border-[#E5E0D5] text-[#6B665E] hover:bg-[#F5F1EA]"
                  data-testid="geocode-address-btn"
                >
                  <LocateFixed className={`w-4 h-4 mr-1 ${geocoding ? 'animate-spin' : ''}`} />
                  {geocoding ? 'Geocoding...' : 'Geocode'}
                </Button>
              </div>
              {parish?.coordinates && (
                <p className="text-xs text-green-600 mt-1">
                  <MapPin className="w-3 h-3 inline mr-1" />
                  Coordinates: {parish.coordinates.lat?.toFixed(4)}, {parish.coordinates.lng?.toFixed(4)}
                </p>
              )}
              {!parish?.coordinates && formData.address && (
                <p className="text-xs text-amber-600 mt-1">
                  <MapPin className="w-3 h-3 inline mr-1" />
                  No coordinates - click Geocode to enable distance-based search
                </p>
              )}
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-['Nunito'] text-[#6B665E]">Office Email</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#6B665E]" />
                <Input 
                  value={formData.office_email}
                  onChange={(e) => setFormData({...formData, office_email: e.target.value})}
                  placeholder="office@parish.org"
                  className="border-[#E5E0D5] font-['Nunito'] text-sm pl-10"
                  data-testid="parish-email-input"
                />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-['Nunito'] text-[#6B665E]">Office Phone</Label>
              <div className="relative">
                <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#6B665E]" />
                <Input 
                  value={formData.office_phone}
                  onChange={(e) => setFormData({...formData, office_phone: e.target.value})}
                  placeholder="(555) 123-4567"
                  className="border-[#E5E0D5] font-['Nunito'] text-sm pl-10"
                  data-testid="parish-phone-input"
                />
              </div>
            </div>
            <div className="space-y-1.5 md:col-span-2">
              <Label className="text-xs font-['Nunito'] text-[#6B665E]">Website</Label>
              <div className="relative">
                <Globe className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#6B665E]" />
                <Input 
                  value={formData.website}
                  onChange={(e) => setFormData({...formData, website: e.target.value})}
                  placeholder="https://www.yourparish.org"
                  className="border-[#E5E0D5] font-['Nunito'] text-sm pl-10"
                  data-testid="parish-website-input"
                />
              </div>
            </div>
            <div className="space-y-1.5 md:col-span-2">
              <Label className="text-xs font-['Nunito'] text-[#6B665E]">Catholic Tradition/Rite</Label>
              <Select value={formData.rite || ""} onValueChange={(v) => setFormData({...formData, rite: v})}>
                <SelectTrigger className="border-[#E5E0D5] text-sm font-['Nunito']" data-testid="parish-rite-select">
                  <div className="flex items-center gap-2">
                    <BookOpen className="w-4 h-4 text-[#6B665E]" />
                    <SelectValue placeholder="Select a tradition/rite" />
                  </div>
                </SelectTrigger>
                <SelectContent className="bg-white">
                  {RITES.map(rite => (
                    <SelectItem key={rite} value={rite} className="font-['Nunito'] text-sm">{rite}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-[10px] text-[#6B665E] font-['Nunito'] mt-1">
                This indicates the liturgical tradition followed by your parish
              </p>
            </div>
            
            {/* Diocese Selection - Searchable */}
            <div className="md:col-span-2">
              <DioceseSearchSelect
                countries={diocesesData.countries}
                diocesesByCountry={diocesesData.dioceses_by_country}
                selectedCountry={selectedCountry}
                selectedDiocese={formData.diocese}
                onCountryChange={(country) => {
                  setSelectedCountry(country);
                  setFormData({...formData, diocese_country: country, diocese: ""});
                }}
                onDioceseChange={(diocese) => setFormData({...formData, diocese})}
              />
            </div>
            
            <div className="space-y-1.5 md:col-span-2">
              <Label className="text-xs font-['Nunito'] text-[#6B665E]">About Our Parish</Label>
              <RichTextEditor
                value={formData.description}
                onChange={(value) => setFormData({...formData, description: value})}
                placeholder="Tell visitors about your parish, its history, and community..."
                minHeight="120px"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Mass Schedules */}
      <ScheduleCard
        title="Mass Schedules"
        icon={<Church className="w-5 h-5 text-[var(--liturgical-primary)]" />}
        schedules={formData.mass_schedules}
        ScheduleDialog={MassScheduleDialog}
        addButtonText="Add Mass"
        addButtonColor="bg-[var(--liturgical-primary)] hover:brightness-90"
        iconBgColor="var(--liturgical-primary)"
        emptyText='No Mass schedules added yet. Click "Add Mass" to add one.'
        onAdd={(s) => addSchedule('mass_schedules', s)}
        onEdit={(idx, s) => editSchedule('mass_schedules', idx, s)}
        onRemove={(idx) => removeSchedule('mass_schedules', idx)}
        onMove={(idx, dir) => moveSchedule('mass_schedules', idx, dir)}
        onDuplicate={(idx) => duplicateSchedule('mass_schedules', idx)}
        renderScheduleInfo={(s) => (
          <>
            <p className="font-['Nunito'] text-sm font-semibold text-[#2D2A26]">
              {s.day} at {s.time}
            </p>
            <p className="text-xs text-[#6B665E] font-['Nunito'] truncate">
              {s.liturgy_type} • {s.language}
              {s.notes && ` • ${s.notes}`}
            </p>
          </>
        )}
      />

      {/* Reconciliation Schedules */}
      <ScheduleCard
        title="Reconciliation (Confession) Schedules"
        icon={<Cross className="w-5 h-5 text-[var(--liturgical-primary)]" />}
        schedules={formData.reconciliation_schedules}
        ScheduleDialog={ReconciliationScheduleDialog}
        addButtonText="Add Confession"
        addButtonColor="bg-[var(--liturgical-primary)] hover:brightness-90"
        iconBgColor="#556B2F"
        emptyText='No Reconciliation schedules added yet. Click "Add Confession" to add one.'
        onAdd={(s) => addSchedule('reconciliation_schedules', s)}
        onEdit={(idx, s) => editSchedule('reconciliation_schedules', idx, s)}
        onRemove={(idx) => removeSchedule('reconciliation_schedules', idx)}
        onMove={(idx, dir) => moveSchedule('reconciliation_schedules', idx, dir)}
        onDuplicate={(idx) => duplicateSchedule('reconciliation_schedules', idx)}
        renderScheduleInfo={(s) => (
          <>
            <p className="font-['Nunito'] text-sm font-semibold text-[#2D2A26]">
              {s.day}: {s.start_time} - {s.end_time}
            </p>
            {s.notes && (
              <p className="text-xs text-[#6B665E] font-['Nunito'] truncate">{s.notes}</p>
            )}
          </>
        )}
      />

      {/* Adoration Schedules */}
      <ScheduleCard
        title="Eucharistic Adoration Schedules"
        icon={<Sun className="w-5 h-5 text-[var(--liturgical-secondary)]" />}
        schedules={formData.adoration_schedules}
        ScheduleDialog={AdorationScheduleDialog}
        addButtonText="Add Adoration"
        addButtonColor="bg-[var(--liturgical-secondary)] hover:brightness-90"
        iconBgColor="#D4AF37"
        emptyText='No Adoration schedules added yet. Click "Add Adoration" to add one.'
        onAdd={(s) => addSchedule('adoration_schedules', s)}
        onEdit={(idx, s) => editSchedule('adoration_schedules', idx, s)}
        onRemove={(idx) => removeSchedule('adoration_schedules', idx)}
        onMove={(idx, dir) => moveSchedule('adoration_schedules', idx, dir)}
        onDuplicate={(idx) => duplicateSchedule('adoration_schedules', idx)}
        renderScheduleInfo={(s) => (
          <>
            <p className="font-['Nunito'] text-sm font-semibold text-[#2D2A26]">
              {s.day}: {s.start_time} - {s.end_time}
              {s.perpetual && <span className="ml-2 text-xs text-[var(--liturgical-secondary)] font-normal">(Perpetual)</span>}
            </p>
            {s.notes && (
              <p className="text-xs text-[#6B665E] font-['Nunito'] truncate">{s.notes}</p>
            )}
          </>
        )}
      />

      {/* Parish Staff Section */}
      <ParishStaffSection
        staff={formData.staff || []}
        onChange={(staff) => setFormData({...formData, staff})}
        parishId={user.parish_id}
      />

      {/* Save Button */}
      <div className="flex justify-end">
        <Button 
          onClick={handleSave}
          disabled={saving}
          className="bg-[var(--liturgical-primary)] hover:brightness-90 text-white font-['Nunito'] font-semibold px-6"
          data-testid="save-parish-btn"
        >
          <Save className="w-4 h-4 mr-2" />
          {saving ? "Saving..." : "Save Changes"}
        </Button>
      </div>
    </div>
  );
}

// Reusable Schedule Card Component
function ScheduleCard({ 
  title, 
  icon, 
  schedules, 
  ScheduleDialog, 
  addButtonText, 
  addButtonColor, 
  iconBgColor,
  emptyText,
  onAdd, 
  onEdit, 
  onRemove, 
  onMove,
  onDuplicate,
  renderScheduleInfo 
}) {
  return (
    <Card className="bg-white border-[#E5E0D5] shadow-sm">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="font-['Crimson_Text'] text-xl text-[#2D2A26] flex items-center gap-2">
            {icon}
            {title}
          </CardTitle>
          <ScheduleDialog 
            onSave={onAdd} 
            trigger={
              <Button size="sm" className={`${addButtonColor} text-white font-['Nunito'] text-xs`}>
                <Plus className="w-3 h-3 mr-1" /> {addButtonText}
              </Button>
            }
          />
        </div>
      </CardHeader>
      <CardContent>
        {schedules.length === 0 ? (
          <div className="text-center py-6 text-[#6B665E] font-['Nunito'] text-sm">
            {emptyText}
          </div>
        ) : (
          <div className="space-y-2">
            {schedules.map((schedule, idx) => (
              <div key={idx} className="flex items-center gap-2 p-3 bg-[#F9F7F2] rounded-md border border-[#E5E0D5]">
                {/* Reorder buttons */}
                <div className="flex flex-col gap-0.5">
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={() => onMove(idx, 'up')}
                    disabled={idx === 0}
                    className="h-5 w-5 p-0 text-[#6B665E] hover:text-[#2D2A26] disabled:opacity-30"
                  >
                    <ChevronUp className="w-3 h-3" />
                  </Button>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={() => onMove(idx, 'down')}
                    disabled={idx === schedules.length - 1}
                    className="h-5 w-5 p-0 text-[#6B665E] hover:text-[#2D2A26] disabled:opacity-30"
                  >
                    <ChevronDown className="w-3 h-3" />
                  </Button>
                </div>
                
                <div 
                  className="w-8 h-8 rounded flex items-center justify-center flex-shrink-0"
                  style={{ backgroundColor: `${iconBgColor}15` }}
                >
                  <Clock className="w-4 h-4" style={{ color: iconBgColor }} />
                </div>
                
                <div className="flex-1 min-w-0">
                  {renderScheduleInfo(schedule)}
                </div>
                
                <div className="flex items-center gap-1">
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={() => onDuplicate(idx)}
                    className="h-8 w-8 p-0 text-[var(--liturgical-primary)] hover:text-[#445622] hover:bg-[var(--liturgical-primary)]/10"
                    title="Duplicate"
                  >
                    <Copy className="w-3.5 h-3.5" />
                  </Button>
                  <ScheduleDialog 
                    onSave={(s) => onEdit(idx, s)} 
                    editData={schedule}
                    trigger={
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        className="h-8 w-8 p-0 text-[#6B665E] hover:text-[var(--liturgical-primary)] hover:bg-[var(--liturgical-primary)]/10"
                        title="Edit"
                      >
                        <Pencil className="w-3.5 h-3.5" />
                      </Button>
                    }
                  />
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={() => onRemove(idx)}
                    className="h-8 w-8 p-0 text-[#991B1B] hover:bg-[#991B1B]/10"
                    title="Delete"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
